
import {
    CheckCircleRounded,
    Close,
    CloudUploadOutlined,
    ExpandMoreRounded,
    InfoOutlined,
    LinkRounded,
    RadioButtonUncheckedOutlined,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { mapTaskKey } from "../canonical/map-keys";
import { agreementForTask, folderForTask, type AgreementCopy } from "../documents/model";

import { colors, palette, radius } from "../theme/tokens";

import { DocumentPill } from "./DocumentPill";
import { PdfPages } from "./PdfPages";
import { internalLast } from "../canonical/internal-steps";
import { PHASE_FILL, PhaseChevrons, PhaseMark, type PhaseState } from "./PhaseChevrons";
import { markdownComponents } from "./ProgramsExplorer";
import { gfmTableComponents } from "./RichText";
import type { WidgetProps } from "./types";

// Onboarding Checklist (the MAP widget; page template and widget type stay
// `map`). Interactive: any user with the room URL can toggle a step complete
// OR upload a file against a step flagged acceptsUpload. State persists to
// /api/rooms/[slug]/state (sidecar JSON in dev, Blob in prod), keyed by widget
// id + stable task id.
//
// Layout (2026-09-05, Bill; shaped by a supplier-persona review, softened and
// recoloured the same day on Bill's second pass):
//   1. Phase chevrons (PhaseChevrons.tsx), tinted by the phase's own count
//      (Bill, 2026-09-24): every step done green, any step done blue, none
//      gray. Position no longer counts (an earlier phase with open steps used
//      to read green); yellow stood in for blue for an hour and was rough on
//      the eyes.
//   2. One collapsible panel per phase with a count in the header. Every
//      in-progress phase is open at rest (Bill, 2026-09-24), else the earliest
//      phase with an open step (currentPhaseIndex). (A "What's next?" card sat
//      between the chevrons and the panels for a day; Bill removed it on
//      2026-09-06.) A phase whose every step is done gets a green header,
//      border and title, the chevrons' dark green (Bill, 2026-09-08); an
//      in-progress phase keeps a white header with the chevrons' blue number
//      mark and a blue count; a done step row is struck through, never tinted.
//   3. One row per step: toggle, title (regular weight), then on the right
//      "<file> uploaded Sep 1" in blue when a file is on the step, the owner
//      pill, a copy-link button and a caret. No done date (Bill, 2026-09-06);
//      every step's copy says who does it ("You send...", "We publish...").
//      The owner pill (Bison / the supplier's name / Both, each on its own
//      tint, dimmed once the step is done) and the All / Bison / <supplier> /
//      Both filter row between the chevrons and the panels are in every room
//      (Bill, 2026-09-24, after a review on Hush Crib behind a since-removed
//      `showOwners` flag); they replace the owner label Bill removed on
//      2026-09-06. The description, template pills and upload control sit in
//      the expandable body; every row starts collapsed.
//
// Current phase = the latest phase with at least one done and one open step,
// else the earliest phase with an open step. All steps done = "Onboarding
// complete".
//
// Rules that carry over: no kind chip (meeting / task / milestone stays in the
// data), step copy never renders a link (markdown anchors come out as plain
// text), and documents are DocumentPill everywhere. Deep link: #task-<taskId>
// opens the phase and the row and scrolls to it.
export type Status = "pending" | "in_progress" | "complete";

interface RoomState {
    taskStatus: Record<string, Status>;
    taskAttachments?: Record<string, string[]>;
    taskCompletedAt?: Record<string, string>;
}

interface UploadEntry {
    name: string;
    uploadedAt: string;
}

type Task = WidgetProps<"map">["widget"]["props"]["phases"][number]["tasks"][number];

// Who owns a step (`task.assignee`; a step without one counts as shared so it
// never disappears from a filter). The pill on the row reads "Bison", the
// supplier's name or "Both", each on its own tint so a reader can tell mine /
// ours / theirs at a glance (Bill, 2026-09-24). Bison sits on the info tint,
// the supplier on purple, a shared step on gold.
type Owner = "bison" | "supplier" | "shared";
type OwnerFilter = "all" | Owner;
type StatusFilter = "all" | "open" | "done";

// The reader's last owner and status picks, per room, in this browser
// (Elijah, 2026-10-29). Storage can be blocked; the defaults then hold.
const filterKey = (slug: string) => `checklist-filters:${slug}`;
function readFilters(slug: string): { owner: OwnerFilter; status: StatusFilter } {
    const fallback = { owner: "all" as OwnerFilter, status: "all" as StatusFilter };
    try {
        const saved = JSON.parse(window.localStorage.getItem(filterKey(slug)) ?? "null");
        const owner = ["all", "bison", "supplier", "shared"].includes(saved?.owner) ? saved.owner : "all";
        const status = ["all", "open", "done"].includes(saved?.status) ? saved.status : "all";
        return saved ? { owner, status } : fallback;
    } catch {
        return fallback;
    }
}
function writeFilters(slug: string, value: { owner: OwnerFilter; status: StatusFilter }) {
    try {
        window.localStorage.setItem(filterKey(slug), JSON.stringify(value));
    } catch {
        // Not remembered; the page still works.
    }
}

function statusMatches(t: ResolvedTask, filter: StatusFilter): boolean {
    return filter === "all" || (filter === "done") === t.done;
}

const OWNER_TINT: Record<Owner, { bg: string; border: string; fg: string }> = {
    bison: { bg: palette.infoBg, border: palette.infoBorder, fg: palette.blueDark },
    supplier: { bg: palette.purpleBg, border: palette.purpleBorder, fg: palette.purpleDark },
    shared: { bg: palette.warningBg, border: palette.warningBorder, fg: palette.goldDark },
};

function ownerOf(task: Task): Owner {
    return task.assignee ?? "shared";
}

function ownerLabel(owner: Owner, supplierName: string): string {
    return owner === "bison" ? "Bison" : owner === "supplier" ? supplierName : "Both";
}

// A filter shows exactly the steps carrying that pill: Bison, the supplier,
// or Both (Bill, 2026-09-24, second pass: Both is its own filter, so the
// side filters no longer fold the shared steps in).
function ownerMatches(task: Task, filter: OwnerFilter): boolean {
    return filter === "all" || ownerOf(task) === filter;
}

export interface ResolvedTask {
    task: Task;
    key: string;
    status: Status;
    done: boolean;
    anchor: string;
}

export interface ResolvedPhase {
    name: string;
    idx: number;
    /** Bison Internal: staff only, collapsed at rest, out of the chevrons. */
    internal: boolean;
    tasks: ResolvedTask[];
    done: number;
    total: number;
    state: PhaseState;
}

export function MAP({ widget, room }: WidgetProps<"map">) {
    // Bison Internal always renders last, whatever order the room stores.
    const phases = useMemo(() => internalLast(widget.props.phases), [widget.props.phases]);
    const slug = room.slug;
    const widgetId = widget.id;
    const supplierName = room.supplier.supplierName;

    const [overrides, setOverrides] = useState<Record<string, Status>>({});
    const [filter, setFilter] = useState<OwnerFilter>(() => readFilters(slug).owner);
    const [status, setStatus] = useState<StatusFilter>(() => readFilters(slug).status);
    const [attachments, setAttachments] = useState<Record<string, string[]>>({});
    const [uploadDates, setUploadDates] = useState<Record<string, string>>({});
    const [hydrated, setHydrated] = useState(false);
    const [openPhases, setOpenPhases] = useState<Set<number>>(new Set());
    const [openTasks, setOpenTasks] = useState<Set<string>>(new Set());
    // The row a #task-<id> deep link named: it flashes light blue once and
    // fades back to white so the reader can see which step the link meant
    // (Bill, 2026-09-09).
    const [flashKey, setFlashKey] = useState<string | null>(null);

    // Initial fetch: keep render deterministic until the user has the
    // server's view of room state. Upload dates come from the uploads list
    // (Blob creation time) so the row can say when a file arrived.
    //
    // The rest state is chosen here, once, from the fetched statuses: the
    // current phase open with every row collapsed (Bill, 2026-09-06), or the
    // phase and row a #task-<id> hash names. Later toggles do not move the
    // panels.
    useEffect(() => {
        let cancelled = false;
        const openAtRest = (taskStatus: Record<string, Status>) => {
            const phasesNow = resolvePhases(phases, widgetId, taskStatus);
            const hash = window.location.hash.replace(/^#/, "");
            const target = hash ? findTask(phasesNow, (t) => t.anchor === hash) : null;
            if (target) {
                setOpenPhases(new Set([target.phase.idx]));
                setOpenTasks(new Set([target.task.key]));
                setFlashKey(target.task.key);
                window.setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
                return;
            }
            // Every phase in progress opens (Bill, 2026-09-24); with none in
            // progress, the earliest phase with an open step. Bison Internal
            // stays collapsed until a staff member opens it (Elijah, 2026-11-02).
            const opened = new Set(phasesNow.filter((p) => p.state === "in_progress" && !p.internal).map((p) => p.idx));
            if (opened.size === 0 && currentPhaseIndex(phasesNow) >= 0) opened.add(currentPhaseIndex(phasesNow));
            // Filters remembered from the last visit open their phases too,
            // the same as picking them would.
            const saved = readFilters(slug);
            if ((saved.owner !== "all" || saved.status !== "all")) {
                phasesNow.forEach((p) => {
                    if (!p.internal && p.tasks.some((t) => ownerMatches(t.task, saved.owner) && statusMatches(t, saved.status))) opened.add(p.idx);
                });
            }
            setOpenPhases(opened);
        };
        fetch(`/api/rooms/${slug}/state`, { cache: "no-store" })
            .then((r) => r.json() as Promise<RoomState>)
            .then((s) => {
                if (cancelled) return;
                setOverrides(s.taskStatus ?? {});
                setAttachments(s.taskAttachments ?? {});
                openAtRest(s.taskStatus ?? {});
                setHydrated(true);
            })
            .catch(() => {
                if (cancelled) return;
                openAtRest({});
                setHydrated(true); // proceed with fixture-only state
            });
        fetch(`/api/rooms/${slug}/uploads`, { cache: "no-store" })
            .then((r) => r.json() as Promise<{ files?: UploadEntry[] }>)
            .then((res) => {
                if (cancelled) return;
                const dates: Record<string, string> = {};
                for (const f of res.files ?? []) dates[f.name] = f.uploadedAt;
                setUploadDates(dates);
            })
            .catch(() => {
                // No dates then; the filename still shows.
            });
        return () => {
            cancelled = true;
        };
    }, [slug, phases, widgetId]);

    const resolved = useMemo(() => resolvePhases(phases, widgetId, overrides), [phases, widgetId, overrides]);


    const openPhase = useCallback((idx: number) => {
        setOpenPhases((prev) => new Set(prev).add(idx));
    }, []);
    const togglePhase = useCallback((idx: number) => {
        setOpenPhases((prev) => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx);
            else next.add(idx);
            return next;
        });
    }, []);
    const toggleTask = useCallback((key: string) => {
        setOpenTasks((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const jumpToPhase = useCallback(
        (idx: number) => {
            openPhase(idx);
            window.setTimeout(() => document.getElementById(`phase-${idx}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
        },
        [openPhase],
    );

    // Picking a side or a status opens every phase that has a matching step,
    // so the reader sees the whole list without expanding panels.
    const applyFilters = useCallback(
        (owner: OwnerFilter, nextStatus: StatusFilter) => {
            setFilter(owner);
            setStatus(nextStatus);
            writeFilters(slug, { owner, status: nextStatus });
            if (owner === "all" && nextStatus === "all") return;
            setOpenPhases((prev) => {
                const opened = new Set(prev);
                resolved.forEach((p) => {
                    if (!p.internal && p.tasks.some((t) => ownerMatches(t.task, owner) && statusMatches(t, nextStatus))) opened.add(p.idx);
                });
                return opened;
            });
        },
        [resolved, slug],
    );
    const pickFilter = useCallback((next: OwnerFilter) => applyFilters(next, status), [applyFilters, status]);
    const pickStatus = useCallback((next: StatusFilter) => applyFilters(filter, next), [applyFilters, filter]);

    const toggle = useCallback(
        (key: string, currentStatus: Status) => {
            const next: Status = currentStatus === "complete" ? "pending" : "complete";
            // Optimistic update; the server stamps the real date.
            setOverrides((prev) => ({ ...prev, [key]: next }));
            fetch(`/api/rooms/${slug}/state`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ taskStatus: { [key]: next } }),
            })
                .then((r) => {
                    if (!r.ok) throw new Error("state write failed");
                })
                .catch(() => {
                    // Revert on failure, keeps the server-of-truth feel.
                    setOverrides((prev) => ({ ...prev, [key]: currentStatus }));
                });
        },
        [slug],
    );

    const onUploaded = useCallback((key: string, filename: string) => {
        setAttachments((prev) => {
            const existing = prev[key] ?? [];
            if (existing.includes(filename)) return prev;
            return { ...prev, [key]: [...existing, filename] };
        });
        setUploadDates((prev) => ({ ...prev, [filename]: new Date().toISOString() }));
    }, []);

    // Under a side filter a phase shows only that side's steps and counts
    // them ("1 of 3 done" is about what is shown); a phase with none is
    // left out. The chevrons stay whole: they are progress, not the filter.
    // The status filter only hides rows: the count stays the side's progress
    // ("2 of 5 done"), or every panel would read Done under Done.
    const shown = useMemo(() => {
        if (filter === "all" && status === "all") return resolved;
        return resolved
            .map((p) => {
                const owned = p.tasks.filter((t) => ownerMatches(t.task, filter));
                const tasks = owned.filter((t) => statusMatches(t, status));
                return { ...p, tasks, done: owned.filter((t) => t.done).length, total: owned.length };
            })
            .filter((p) => p.tasks.length > 0);
    }, [resolved, filter, status]);

    return (
        <Stack spacing={4}>
            <PhaseChevrons
                phases={resolved.filter((p) => !p.internal).map((p) => ({ name: p.name, state: p.state }))}
                onSelect={jumpToPhase}
            />

            <Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: "wrap", justifyContent: "space-between", rowGap: 1, mt: -2 }}>
                <PillGroup
                    label="Show steps for"
                    value={filter}
                    onChange={pickFilter}
                    options={[
                        { value: "all", label: "All owners", tint: QUIET_TINT },
                        { value: "bison", label: "Bison", tint: OWNER_TINT.bison },
                        { value: "supplier", label: supplierName, tint: OWNER_TINT.supplier },
                        { value: "shared", label: "Both", tint: OWNER_TINT.shared },
                    ]}
                />
                <PillGroup
                    label="Show steps that are"
                    value={status}
                    onChange={pickStatus}
                    options={[
                        { value: "all", label: "All statuses", tint: QUIET_TINT },
                        { value: "open", label: "Open", tint: QUIET_TINT },
                        { value: "done", label: "Done", tint: QUIET_TINT },
                    ]}
                />
            </Stack>

            {shown.length === 0 && (
                <Typography sx={{ color: colors.darkGray, fontSize: "0.95rem" }}>No steps match these filters.</Typography>
            )}

            <Stack spacing={2}>
                {shown.map((phase) => (
                    <PhasePanel
                        key={phase.idx}
                        phase={phase}
                        open={openPhases.has(phase.idx)}
                        onToggle={() => togglePhase(phase.idx)}
                    >
                        {phase.tasks.map((t) => (
                            <TaskRow
                                key={t.key}
                                resolved={t}
                                slug={slug}
                                hydrated={hydrated}
                                expanded={openTasks.has(t.key)}
                                flash={flashKey === t.key}
                                owner={{ label: ownerLabel(ownerOf(t.task), supplierName), tint: OWNER_TINT[ownerOf(t.task)] }}
                                onExpand={() => toggleTask(t.key)}
                                onToggle={() => toggle(t.key, t.status)}
                                attachments={attachments[t.key] ?? []}
                                agreement={agreementForTask(room, t.task.taskId)}
                                uploadDates={uploadDates}
                                onUploaded={(name) => onUploaded(t.key, name)}
                            />
                        ))}
                    </PhasePanel>
                ))}
            </Stack>
        </Stack>
    );
}

// ─── Owner filter ──────────────────────────────────────────────────────

// Pills under the chevrons: owners on the left (All / Bison / <supplier> /
// Both), statuses on the right (Elijah, 2026-10-29). The picked owner
// takes its side's tint; an "all" pick or a status is a quiet gray (a navy
// fill was distracting, Bill 2026-09-24); the others are outlined.
const QUIET_TINT = { bg: colors.lightGray, border: colors.mediumGray, fg: colors.navy };

function PillGroup<T extends string>({
    label,
    value,
    onChange,
    options,
}: {
    label: string;
    value: T;
    onChange: (next: T) => void;
    options: { value: T; label: string; tint: { bg: string; border: string; fg: string } }[];
}) {
    return (
        <Stack direction="row" useFlexGap spacing={1} role="group" aria-label={label} sx={{ flexWrap: "wrap" }}>
            {options.map((o) => {
                const active = o.value === value;
                return (
                    <Box
                        key={o.value}
                        component="button"
                        type="button"
                        aria-pressed={active}
                        onClick={() => onChange(o.value)}
                        sx={{
                            all: "unset",
                            boxSizing: "border-box",
                            cursor: "pointer",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "999px",
                            border: `1px solid ${active ? o.tint.border : colors.line}`,
                            bgcolor: active ? o.tint.bg : colors.white,
                            color: active ? o.tint.fg : colors.darkGray,
                            fontSize: "0.8rem",
                            fontWeight: 500,
                            lineHeight: 1.5,
                            whiteSpace: "nowrap",
                            transition: "background-color 120ms ease-out, border-color 120ms ease-out",
                            "&:hover": { bgcolor: active ? o.tint.bg : colors.lightGray },
                            "&:focus-visible": { outline: `2px solid ${colors.brandOrange}`, outlineOffset: 2 },
                        }}
                    >
                        {o.label}
                    </Box>
                );
            })}
        </Stack>
    );
}

// The pill on a step row, right before the copy-link button.
function OwnerPill({ label, tint, dim }: { label: string; tint: { bg: string; border: string; fg: string }; dim: boolean }) {
    return (
        <Box
            component="span"
            sx={{
                alignSelf: "center",
                px: 1,
                py: "1px",
                borderRadius: "999px",
                border: `1px solid ${tint.border}`,
                bgcolor: tint.bg,
                color: tint.fg,
                fontSize: "0.72rem",
                fontWeight: 500,
                lineHeight: 1.6,
                whiteSpace: "nowrap",
                opacity: dim ? 0.55 : 1,
            }}
        >
            {label}
        </Box>
    );
}

// Resolve the widget's phases against the live statuses: per-task key,
// status and anchor, per-phase counts and the chevron state.
export function resolvePhases(
    phases: WidgetProps<"map">["widget"]["props"]["phases"],
    widgetId: string,
    overrides: Record<string, Status>,
): ResolvedPhase[] {
    const out: ResolvedPhase[] = phases.map((phase, phaseIdx) => {
        const tasks = phase.tasks.map((task, taskIdx) => {
            const key = mapTaskKey({ widgetId, task, phaseIdx, taskIdx });
            const status = overrides[key] ?? ((task.status ?? "pending") as Status);
            return {
                task,
                key,
                status,
                done: status === "complete",
                anchor: `task-${task.taskId ?? key.replace(/[^a-z0-9-]/gi, "-")}`,
            };
        });
        const done = tasks.filter((t) => t.done).length;
        return { name: phase.name, idx: phaseIdx, internal: phase.internal === true, tasks, done, total: tasks.length, state: "upcoming" };
    });
    // State by the phase's own count (Bill, 2026-09-24): every step done is
    // green, any step done is yellow, none is gray. Position no longer counts,
    // so a phase before the one in hand with open steps is yellow, not green.
    for (const p of out) {
        p.state = p.total > 0 && p.done === p.total ? "complete" : p.done > 0 ? "in_progress" : "upcoming";
    }
    return out;
}

// Latest phase that is genuinely in progress (some done, some open); failing
// that, the earliest phase with an open step; -1 when everything is done.
// Bison Internal never counts: it is not the supplier's progress.
export function currentPhaseIndex(phases: ResolvedPhase[]): number {
    let inProgress = -1;
    let firstOpen = -1;
    phases.forEach((p) => {
        if (p.internal) return;
        const open = p.total - p.done;
        if (open > 0 && p.done > 0) inProgress = p.idx;
        if (open > 0 && firstOpen === -1) firstOpen = p.idx;
    });
    return inProgress !== -1 ? inProgress : firstOpen;
}

function findTask(
    phases: ResolvedPhase[],
    pred: (t: ResolvedTask) => boolean,
): { phase: ResolvedPhase; task: ResolvedTask } | null {
    for (const phase of phases) {
        const task = phase.tasks.find(pred);
        if (task) return { phase, task };
    }
    return null;
}

// ─── Phase panel ───────────────────────────────────────────────────────

function PhasePanel({
    phase,
    open,
    onToggle,
    children,
}: {
    phase: ResolvedPhase;
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}) {
    // From the counts the panel shows (a filter narrows them), not the
    // chevron's state.
    const markState: PhaseState = phase.total > 0 && phase.done === phase.total ? "complete" : phase.done > 0 ? "in_progress" : "upcoming";
    const fill = PHASE_FILL[markState];
    // The panel goes green only when every step in it is done (Bill,
    // 2026-09-08); an in-progress phase carries the chevrons' blue number
    // and a blue count on the infoBg tint (white from 2026-09-24 to 10-28). A done step row stays white and is struck through.
    const finished = phase.total > 0 && phase.done === phase.total; // counts, not state: a filter narrows the counts
    // An in-progress header sits on the infoBg tint (Elijah, 2026-10-28; trialled on hush-crib).
    const tinted = markState === "in_progress";
    // An expanded phase with no step done takes the "upcoming" chevron's lightGray, and stays white when collapsed so it never reads disabled (Elijah, 2026-10-29; trialled on casartis-living).
    const grayed = open && markState === "upcoming";
    const count =
        finished ? "Done" : phase.done === 0 ? `${phase.total} ${plural(phase.total, "step")}` : `${phase.done} of ${phase.total} done`;
    return (
        <Box
            id={`phase-${phase.idx}`}
            sx={{
                border: `1px solid ${finished ? palette.successBorder : colors.line}`,
                borderRadius: "10px",
                bgcolor: colors.white,
                overflow: "hidden",
                scrollMarginTop: 16,
            }}
        >
            <Box
                component="button"
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                aria-controls={`phase-${phase.idx}-tasks`}
                sx={{
                    all: "unset",
                    boxSizing: "border-box",
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: { xs: 2, md: 2.5 },
                    py: 1.5,
                    cursor: "pointer",
                    bgcolor: finished ? palette.successBg : tinted ? palette.infoBg : grayed ? PHASE_FILL.upcoming.bg : colors.white,
                    "&:hover": { bgcolor: finished ? palette.successBg : tinted ? palette.infoBg : grayed ? PHASE_FILL.upcoming.bg : colors.lightGray },
                    "&:focus-visible": { outline: `2px solid ${colors.brandOrange}`, outlineOffset: -2 },
                }}
            >
                <PhaseMark idx={phase.idx} state={markState} size={26} />
                <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 500, fontSize: "1rem", color: finished ? palette.green : markState === "upcoming" ? colors.darkGray : colors.navy }}>
                    {phase.name}
                    {phase.internal && (
                        <Box component="span" sx={{ ml: 1, fontSize: "0.75rem", fontWeight: 500, color: colors.darkGray, whiteSpace: "nowrap" }}>
                            Only Bison staff see this section
                        </Box>
                    )}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 500, whiteSpace: "nowrap", color: fill.fg }}>
                    {count}
                </Typography>
                <ExpandMoreRounded
                    sx={{
                        color: colors.mediumGray,
                        transition: "transform 150ms ease-out",
                        transform: open ? "rotate(180deg)" : "none",
                    }}
                />
            </Box>
            {open && (
                <Box id={`phase-${phase.idx}-tasks`} sx={{ borderTop: `1px solid ${colors.lineSoft}` }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

// ─── Task row ──────────────────────────────────────────────────────────

// Deep-link flash: hold the light blue for a beat, then a slow fade to white.
const taskFlash = keyframes`
    0%, 25% { background-color: #D7E8FE; } /* midpoint of palette.infoBg and infoBorder (Bill, 2026-09-09) */
    100% { background-color: ${colors.white}; }
`;

interface TaskRowProps {
    resolved: ResolvedTask;
    slug: string;
    hydrated: boolean;
    expanded: boolean;
    flash?: boolean;
    /** Owner pill (label + tint); absent when the room does not show owners. */
    owner?: { label: string; tint: { bg: string; border: string; fg: string } };
    onExpand: () => void;
    onToggle: () => void;
    attachments: string[];
    /** The room's own copy of the agreement this step signs (Agreements page list). */
    agreement?: AgreementCopy;
    uploadDates: Record<string, string>;
    onUploaded: (filename: string) => void;
}

function TaskRow({
    resolved,
    slug,
    hydrated,
    expanded,
    flash = false,
    owner,
    onExpand,
    onToggle,
    attachments,
    agreement,
    uploadDates,
    onUploaded,
}: TaskRowProps) {
    const { task, done, anchor } = resolved;
    const tooltip = done ? "Mark as not done" : "Mark as done";
    const [copied, setCopied] = useState(false);

    const copyLink = () => {
        const url = `${window.location.origin}${window.location.pathname}#${anchor}`;
        navigator.clipboard?.writeText(url).then(
            () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
            },
            () => undefined,
        );
    };

    // Right-hand meta: the latest file that arrived (blue). No done date on
    // the row (Bill, 2026-09-06).
    const meta = useMemo(() => {
        if (attachments.length > 0) {
            const latest = attachments[attachments.length - 1];
            const when = uploadDates[latest];
            const name = attachments.length > 1 ? `${attachments.length} files` : latest;
            return { text: when ? `${name} uploaded ${formatShortDate(when)}` : `${name} uploaded`, color: palette.blueDark };
        }
        return null;
    }, [attachments, uploadDates]);

    const hasBody = Boolean(task.description || task.acceptsUpload || agreement || (task.attachments && task.attachments.length > 0) || (task.links && task.links.length > 0) || task.guide || task.dueDate);

    return (
        <Box
            id={anchor}
            sx={{
                bgcolor: colors.white,
                "& + &": { borderTop: `1px solid ${colors.lineSoft}` },
                scrollMarginTop: 16,
                transition: "background-color 150ms ease-out",
                animation: flash ? `${taskFlash} 1.5s ease-out 250ms both` : undefined,
            }}
        >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, px: { xs: 1.5, md: 2 }, py: 1 }}>
                <Tooltip title={tooltip} disableInteractive>
                    <span>
                        <IconButton
                            size="small"
                            onClick={onToggle}
                            disabled={!hydrated}
                            aria-label={tooltip}
                            sx={{ color: done ? palette.green : colors.line, "&:hover": { color: done ? palette.green : colors.mediumGray } }}
                        >
                            {done ? <CheckCircleRounded fontSize="small" /> : <RadioButtonUncheckedOutlined fontSize="small" />}
                        </IconButton>
                    </span>
                </Tooltip>
                <Box
                    component={hasBody ? "button" : "div"}
                    type={hasBody ? "button" : undefined}
                    onClick={hasBody ? onExpand : undefined}
                    aria-expanded={hasBody ? expanded : undefined}
                    sx={{
                        all: "unset",
                        boxSizing: "border-box",
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "baseline",
                        flexWrap: "wrap",
                        gap: 1,
                        rowGap: 0.25,
                        py: 0.5,
                        cursor: hasBody ? "pointer" : "default",
                        "&:focus-visible": { outline: `2px solid ${colors.brandOrange}`, outlineOffset: 2, borderRadius: "6px" },
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontWeight: 500,
                            fontSize: "0.95rem",
                            color: done ? colors.mediumGray : colors.navy,
                            textDecoration: done ? "line-through" : "none",
                        }}
                    >
                        {task.title}
                    </Typography>
                    {meta && (
                        <Typography variant="caption" component="span" sx={{ ml: { md: "auto" }, fontWeight: 500, whiteSpace: "nowrap", color: meta.color }}>
                            {meta.text}
                        </Typography>
                    )}
                </Box>
                {owner && <OwnerPill label={owner.label} tint={owner.tint} dim={done} />}
                <Tooltip title={copied ? "Link copied" : "Copy link to this step"} disableInteractive>
                    <IconButton size="small" onClick={copyLink} aria-label="Copy link to this step" sx={{ color: colors.line, "&:hover": { color: colors.mediumGray } }}>
                        <LinkRounded fontSize="small" />
                    </IconButton>
                </Tooltip>
                {hasBody && (
                    <IconButton
                        size="small"
                        onClick={onExpand}
                        aria-label={expanded ? "Collapse step" : "Expand step"}
                        sx={{ color: colors.mediumGray }}
                    >
                        <ExpandMoreRounded
                            fontSize="small"
                            sx={{ transition: "transform 150ms ease-out", transform: expanded ? "rotate(180deg)" : "none" }}
                        />
                    </IconButton>
                )}
            </Box>
            {hasBody && expanded && (
                <Box sx={{ pl: { xs: 6.5, md: 7 }, pr: { xs: 2, md: 3 }, pb: 2 }}>
                    {task.description && (
                        <Box
                            sx={{
                                color: "text.secondary",
                                "& p": { mt: 0, mb: 0.5 },
                                "& p:last-of-type": { mb: 0 },
                                fontSize: "0.875rem",
                            }}
                        >
                            <ReactMarkdown components={stepMarkdownComponents}>{task.description}</ReactMarkdown>
                        </Box>
                    )}
                    {agreement && <AgreementDownloads agreement={agreement} />}
                    {task.attachments && task.attachments.length > 0 && <TaskDownloads attachments={task.attachments} />}
                    {task.links && task.links.length > 0 && <TaskLinks links={task.links} />}
                    {task.guide && <TaskGuide guide={task.guide} />}
                    {task.acceptsUpload && (
                        <TaskUploadControl slug={slug} taskKey={resolved.key} folder={folderForTask(task.taskId)} attachments={attachments} onUploaded={onUploaded} label={agreement ? "Upload signed copy" : undefined} />
                    )}
                    {task.dueDate && (
                        <Typography variant="caption" sx={{ display: "block", mt: 1, fontWeight: 500 }}>
                            Due {formatDueDate(task.dueDate)}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

interface TaskDownloadsProps {
    attachments: { name: string; url: string }[];
}

function TaskDownloads({ attachments }: TaskDownloadsProps) {
    return (
        <Box sx={{ mt: 1.5 }}>
            <SectionHeader>Templates</SectionHeader>
            <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: "wrap" }}>
                {attachments.map((file) => (
                    <DocumentPill key={file.url} href={file.url} label={file.name} />
                ))}
            </Stack>
        </Box>
    );
}

// Web pages a step points at (the supplier portal, its user guide): pills under
// "Links", so the step copy stays link-free.
function TaskLinks({ links }: { links: { name: string; url: string }[] }) {
    return (
        <Box sx={{ mt: 1.5 }}>
            <SectionHeader>Links</SectionHeader>
            <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: "wrap" }}>
                {links.map((link) =>
                    // `page:documents?folder=x` is a page in this room; a relative
                    // href resolves against the checklist page's own URL.
                    link.url.startsWith("page:") ? (
                        <DocumentPill key={link.url} href={link.url.slice(5)} label={link.name} />
                    ) : (
                        <DocumentPill key={link.url} href={link.url} label={link.name} external />
                    ),
                )}
            </Stack>
        </Box>
    );
}

// A step's requirements: a pill under "Requirements" that opens a popup in
// the Programs modal's style (same paper, header, markdown and paginated PDF
// viewer, plus GFM tables); `after` sits below the PDFs for the secondary
// notes (2026-10-30, Elijah, trial on benzara's COI step).
interface StepGuide {
    label: string;
    title: string;
    summary?: string;
    body: string;
    documents?: { title: string; url: string }[];
    after?: string;
}

const guideMarkdown = { ...markdownComponents, ...gfmTableComponents };

function TaskGuide({ guide }: { guide: StepGuide }) {
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    return (
        <Box sx={{ mt: 1.5 }}>
            <SectionHeader>Requirements</SectionHeader>
            <Chip
                size="small"
                icon={<InfoOutlined fontSize="small" />}
                label={guide.label}
                clickable
                onClick={() => setOpen(true)}
                sx={{
                    height: 26,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    borderRadius: "6px",
                    bgcolor: palette.infoBg,
                    color: palette.blueDark,
                    border: `1px solid ${palette.infoBorder}`,
                    "& .MuiChip-icon": { color: palette.blueDark },
                }}
            />
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="md"
                fullWidth
                fullScreen={fullScreen}
                scroll="paper"
                slotProps={{ paper: { sx: { borderRadius: fullScreen ? 0 : `${radius.lg}px` } } }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        px: { xs: 2.5, md: 4 },
                        pt: { xs: 2.5, md: 3.5 },
                        pb: 2,
                        borderBottom: 1,
                        borderColor: "divider",
                    }}
                >
                    <Typography variant="h4" component="h2" sx={{ flex: 1, minWidth: 0 }}>
                        {guide.title}
                    </Typography>
                    <IconButton onClick={() => setOpen(false)} aria-label="Close" size="small">
                        <Close />
                    </IconButton>
                </Box>
                <DialogContent sx={{ px: { xs: 2.5, md: 4 }, py: 3 }}>
                    {guide.summary && (
                        <Typography variant="body1" sx={{ mb: 2.5, color: "text.secondary" }}>
                            {guide.summary}
                        </Typography>
                    )}
                    <Box sx={{ "& > *:first-of-type": { mt: 0 }, "& > *:last-child": { mb: 0 } }}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]} components={guideMarkdown}>{guide.body}</ReactMarkdown>
                    </Box>
                    {guide.documents && guide.documents.length > 0 && (
                        <Stack spacing={3} sx={{ mt: 3 }}>
                            {guide.documents.map((doc) => (
                                <PdfPages key={doc.url} url={doc.url} title={doc.title} />
                            ))}
                        </Stack>
                    )}
                    {guide.after && (
                        <Box sx={{ mt: 3, "& > *:last-child": { mb: 0 } }}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={guideMarkdown}>{guide.after}</ReactMarkdown>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}

// A signing step carries the agreement itself as one named pill (Bill, 2026-10-14: no separate Download pill).
function AgreementDownloads({ agreement }: { agreement: AgreementCopy }) {
    return (
        <Box sx={{ mt: 1.5 }}>
            <SectionHeader>Document</SectionHeader>
            <Stack direction="row" useFlexGap spacing={0.75} sx={{ flexWrap: "wrap" }}>
                <DocumentPill href={agreement.url} label={agreement.title} />
            </Stack>
        </Box>
    );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
    return (
        <Typography
            variant="caption"
            sx={{
                display: "block",
                color: "text.secondary",
                fontWeight: 600,
                fontSize: "0.7rem",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                mb: 0.75,
            }}
        >
            {children}
        </Typography>
    );
}

interface TaskUploadControlProps {
    slug: string;
    taskKey: string;
    /** Documents-page folder this task's files are filed in (documents/model.ts). */
    folder?: string;
    attachments: string[];
    onUploaded: (filename: string) => void;
    /** Button text before any file is on the step; "Upload file" by default. */
    label?: string;
}

function TaskUploadControl({ slug, taskKey, folder, attachments, onUploaded, label = "Upload file" }: TaskUploadControlProps) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handlePick = () => {
        setError(null);
        inputRef.current?.click();
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting the same file later
        if (!file) return;

        setUploading(true);
        setError(null);
        try {
            const form = new FormData();
            form.append("file", file);
            // The Documents page files the upload by folder and task (2026-09-06).
            if (folder) form.append("folder", folder);
            form.append("taskKey", taskKey);
            const res = await fetch(`/api/rooms/${slug}/uploads`, {
                method: "POST",
                body: form,
            });
            if (!res.ok) {
                const body = (await res.json().catch(() => null)) as { error?: string } | null;
                throw new Error(body?.error ?? `upload failed (${res.status})`);
            }
            const result = (await res.json()) as { name: string };
            // Persist task->file binding so the badge survives reload.
            await fetch(`/api/rooms/${slug}/state`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    taskAttachments: { [taskKey]: { add: [result.name] } },
                }),
            }).catch(() => {
                // Best-effort: the file is already on the Documents page even
                // if we fail to remember which task it came from.
            });
            onUploaded(result.name);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box sx={{ mt: 1.5 }}>
            <SectionHeader>Uploads</SectionHeader>
            <input ref={inputRef} type="file" hidden onChange={handleChange} aria-label="Upload file for this task" />
            <Stack direction="row" useFlexGap spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={uploading ? <CircularProgress size={14} thickness={5} /> : <CloudUploadOutlined fontSize="small" />}
                    onClick={handlePick}
                    disabled={uploading}
                    sx={{ textTransform: "none", fontWeight: 500, fontSize: "0.78rem", py: 0.5 }}
                >
                    {uploading ? "Uploading…" : attachments.length > 0 ? "Replace / add file" : label}
                </Button>
                {attachments.map((name) => (
                    <DocumentPill key={name} href={`/api/rooms/${slug}/uploads/${encodeURIComponent(name)}`} label={name} />
                ))}
            </Stack>
            {error && (
                <Typography variant="caption" sx={{ display: "block", color: "error.main", mt: 0.5 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}

function plural(n: number, word: string): string {
    return n === 1 ? word : `${word}s`;
}

// "Mar 4", with the year only when it is not this year.
function formatShortDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

function formatDueDate(input: string): string {
    const m = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
        const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
        if (!Number.isNaN(d.getTime())) {
            return d.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
            });
        }
    }
    return input;
}

// Markdown renderer for step descriptions. Step copy never renders a link:
// a `[text](page:slug)` or `[text](https://...)` in a description (canonical
// or a per-room override) comes out as the plain text. Templates and uploads
// are the pills, and they stay clickable.
export const stepMarkdownComponents = {
    p: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="body2" component="p" sx={{ color: "text.secondary" }}>
            {children}
        </Typography>
    ),
    a: ({ children }: { children?: React.ReactNode }) => <>{children}</>,
    strong: ({ children }: { children?: React.ReactNode }) => (
        <Box component="strong" sx={{ fontWeight: 600, color: "text.primary" }}>
            {children}
        </Box>
    ),
};
